import * as path from "path";
import * as fs from "fs";

// 类型定义
export interface DetectLanguagesOptions {
  ignoredDirs?: Set<string>;
  topN?: number;
}

// 文件扩展名到语言的完整映射
const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  ".py": "python",
  ".js": "javascript",
  ".ts": "typescript",
  ".jsx": "javascript",
  ".tsx": "typescript",
  ".java": "java",
  ".kt": "kotlin",
  ".go": "go",
  ".rs": "rust",
  ".c": "c",
  ".cpp": "cpp",
  ".h": "c",
  ".hpp": "cpp",
  ".cs": "csharp",
  ".swift": "swift",
  ".m": "objectivec",
  ".php": "php",
  ".rb": "ruby",
  ".sh": "bash",
  ".pl": "perl",
  ".lua": "lua",
  ".scala": "scala",
  ".hs": "haskell",
  ".r": "r",
  ".dart": "dart",
  ".el": "elisp",
  ".erl": "erlang",
  ".ex": "elixir",
  ".jl": "julia",
  ".clj": "clojure",
  ".f90": "fortran",
  ".groovy": "groovy",
  ".scm": "scheme",
  ".sql": "sql",
  ".vb": "vbnet",
  ".xml": "xml",
  ".yaml": "yaml",
  ".yml": "yaml",
  ".json": "json",
  ".html": "html",
  ".css": "css",
  ".scss": "scss",
  ".less": "less",
  ".md": "markdown",
  ".tex": "latex",
  // 可以继续添加更多扩展名映射
};

/**
 * 检测目录中主导的编程语言
 * @param root 要分析的根目录路径
 * @param options 配置选项
 * @returns 按使用频率排序的语言数组
 */
export function detectDominantLanguages(
  root: string = ".",
  options: DetectLanguagesOptions = {},
): string[] {
  const { ignoredDirs = new Set<string>(), topN = 1 } = options;
  const languageCounter = new Map<string, number>();

  // 递归遍历目录的辅助函数
  const walkDirectory = (dirPath: string) => {
    try {
      const entries = fs.readdirSync(dirPath);

      for (const entry of entries) {
        const fullPath = path.join(dirPath, entry);

        try {
          const stat = fs.statSync(fullPath);

          if (stat.isDirectory()) {
            if (!ignoredDirs.has(entry)) {
              walkDirectory(fullPath);
            }
          } else if (stat.isFile()) {
            const extension = path.extname(entry).toLowerCase();
            if (extension in EXTENSION_TO_LANGUAGE) {
              const language = EXTENSION_TO_LANGUAGE[extension];
              languageCounter.set(
                language,
                (languageCounter.get(language) || 0) + 1,
              );
            }
          }
        } catch (error) {
          console.warn(`无法访问路径: ${fullPath}`, error);
        }
      }
    } catch (error) {
      console.warn(`无法读取目录: ${dirPath}`, error);
    }
  };

  // 开始遍历
  walkDirectory(path.resolve(root));

  if (languageCounter.size === 0) {
    return [];
  }

  // TypeScript 和 JavaScript 特殊处理
  // 如果项目中同时存在 TypeScript 和 JavaScript，优先返回 TypeScript
  const tsCount = languageCounter.get("typescript") || 0;
  const jsCount = languageCounter.get("javascript") || 0;

  // 如果 TypeScript 文件数量不为零，并且 JavaScript 和 TypeScript 文件数量接近
  // (TypeScript 文件数量 >= JavaScript 文件数量的 40%)，则优先使用 TypeScript
  if (tsCount > 0 && tsCount >= jsCount * 0.4) {
    // 增加 TypeScript 的权重
    languageCounter.set("typescript", tsCount + jsCount * 0.5);

    // 如果 JavaScript 文件很少，直接返回 TypeScript
    if (tsCount > jsCount * 0.8 && topN === 1) {
      return ["typescript"];
    }
  }

  // 转换为数组并排序
  const sortedLanguages = Array.from(languageCounter.entries()).sort(
    (a, b) => b[1] - a[1],
  );

  // 处理返回结果
  if (topN === 1) {
    const maxCount = sortedLanguages[0][1];
    return sortedLanguages
      .filter(([_, count]) => count === maxCount)
      .map(([language]) => language);
  }

  return sortedLanguages.slice(0, topN).map(([language]) => language);
}

/**
 * 获取规范化后的绝对路径
 * @param pathSegments 路径片段
 * @returns 规范化后的绝对路径
 */
export function resolvePath(...pathSegments: string[]): string {
  return path.resolve(...pathSegments);
}

/**
 * 检查路径是否存在
 * @param targetPath 要检查的路径
 * @returns 是否存在
 */
export function pathExists(targetPath: string): boolean {
  try {
    fs.accessSync(targetPath);
    return true;
  } catch {
    return false;
  }
}

/**
 * 获取指定语言对应的文件扩展名
 * @param lang 语言名称
 * @returns 逗号分隔的扩展名字符串（不带点）
 */
export function getLanguageFileExtensions(lang: string): string {
  const extensionMap: Record<string, string> = {
    c: "c,h",
    cpp: "cpp,hpp,cc,hxx",
    python: "py,pyw",
    typescript: "ts,tsx",
    javascript: "js,jsx,mjs,cjs",
    java: "java",
    go: "go",
    rust: "rs",
    ruby: "rb",
    php: "php",
    swift: "swift",
    kotlin: "kt,kts",
    // 可以继续添加更多语言映射
  };

  // 转换为小写比较，确保大小写不敏感
  const normalizedLang = lang.toLowerCase();

  // 返回映射值或默认原样返回（保持与Python版本相同行为）
  return extensionMap[normalizedLang] || lang;
}
